import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
// @ts-expect-error - react-cytoscapejs doesn't have type definitions
import CytoscapeComponent from 'react-cytoscapejs'
import type { Core, ElementDefinition } from 'cytoscape'
import { ZoomIn, ZoomOut, Maximize2 } from 'lucide-react'
import { Button } from './ui/Button'

// Responsive sizing configuration based on screen width
interface ResponsiveConfig {
  graphHeight: number
  nodeFontSize: number
  nodeSize: number
  edgeFontSize: number
  legendScale: number
}

function getResponsiveConfig(width: number): ResponsiveConfig {
  if (width >= 1920) {
    // Full HD and above - larger everything
    return { graphHeight: 550, nodeFontSize: 18, nodeSize: 52, edgeFontSize: 16, legendScale: 1 }
  } else if (width >= 1440) {
    // Desktop - medium-large
    return { graphHeight: 480, nodeFontSize: 16, nodeSize: 46, edgeFontSize: 15, legendScale: 0.9 }
  } else if (width >= 1280) {
    // Small desktop - medium (800px height screens)
    return { graphHeight: 420, nodeFontSize: 14, nodeSize: 40, edgeFontSize: 13, legendScale: 0.8 }
  } else if (width >= 1024) {
    // Tablet landscape - smaller
    return { graphHeight: 360, nodeFontSize: 13, nodeSize: 36, edgeFontSize: 12, legendScale: 0.75 }
  } else {
    // Mobile/small screens - compact
    return { graphHeight: 300, nodeFontSize: 12, nodeSize: 32, edgeFontSize: 11, legendScale: 0.7 }
  }
}

interface TooltipState {
  visible: boolean
  x: number
  y: number
  proteinId: string
  product: string
}

interface ProteinInteraction {
  protein1_id: string
  protein1_product?: string
  protein2_id: string
  protein2_product?: string
  ipsae: number
  ipsae_min: number
  iptm: number
  is_homo: boolean
}

interface InteractionNetworkProps {
  clusterId: string
  interactions: ProteinInteraction[]
  className?: string
}

/**
 * Interactive protein-protein interaction network using Cytoscape.js
 * - Zoom/pan enabled
 * - Click on nodes → navigate to protein page
 * - Click on edges → navigate to complex detail page
 */
export function InteractionNetwork({ clusterId, interactions, className = '' }: InteractionNetworkProps) {
  const navigate = useNavigate()
  const cyRef = useRef<Core | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const initializedRef = useRef<boolean>(false) // Track if cytoscape has been initialized
  const [tooltip, setTooltip] = useState<TooltipState>({
    visible: false,
    x: 0,
    y: 0,
    proteinId: '',
    product: '',
  })

  // Responsive sizing based on window width
  const [responsiveConfig, setResponsiveConfig] = useState<ResponsiveConfig>(() =>
    getResponsiveConfig(typeof window !== 'undefined' ? window.innerWidth : 1280)
  )

  // Update config on window resize
  useEffect(() => {
    const handleResize = () => {
      setResponsiveConfig(getResponsiveConfig(window.innerWidth))
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Build nodes and edges from interactions
  const elements = useMemo<ElementDefinition[]>(() => {
    const nodeSet = new Map<string, { id: string; label: string; product: string }>()
    const edges: ElementDefinition[] = []

    interactions.forEach((interaction, idx) => {
      // Add nodes
      if (!nodeSet.has(interaction.protein1_id)) {
        nodeSet.set(interaction.protein1_id, {
          id: interaction.protein1_id,
          label: interaction.protein1_id,
          product: interaction.protein1_product || 'Unknown',
        })
      }
      if (!nodeSet.has(interaction.protein2_id)) {
        nodeSet.set(interaction.protein2_id, {
          id: interaction.protein2_id,
          label: interaction.protein2_id,
          product: interaction.protein2_product || 'Unknown',
        })
      }

      // Calculate edge color based on ipTM score (0.6-1.0 range)
      // Gradient from pale salmon (#f5c2aa) to deep red (#c53334) - matching SVG reference
      const getEdgeColor = (iptm: number, isHomo: boolean): string => {
        // Homodimers use blue color
        if (isHomo) {
          return '#3b82f6' // Blue for homodimers
        }
        // Clamp iptm to 0.6-1.0 range and interpolate
        const t = Math.max(0, Math.min(1, (iptm - 0.6) / 0.4))
        // Interpolate between #f5c2aa (pale salmon) and #c53334 (deep red)
        const r = Math.round(245 + (197 - 245) * t) // 245 -> 197
        const g = Math.round(194 + (51 - 194) * t)  // 194 -> 51
        const b = Math.round(170 + (52 - 170) * t)  // 170 -> 52
        return `rgb(${r}, ${g}, ${b})`
      }

      // Add edge
      edges.push({
        data: {
          id: `edge-${idx}`,
          source: interaction.protein1_id,
          target: interaction.protein2_id,
          iptm: interaction.iptm,
          ipsae: interaction.ipsae,
          ipsae_min: interaction.ipsae_min,
          is_homo: interaction.is_homo,
          protein1_id: interaction.protein1_id,
          protein2_id: interaction.protein2_id,
          // Edge width based on ipTM score (0.6-1.0 range)
          width: Math.max(3, (interaction.iptm - 0.5) * 14),
          // Label showing ipTM value
          label: interaction.iptm.toFixed(2),
          // Pre-computed color based on ipTM
          color: getEdgeColor(interaction.iptm, interaction.is_homo),
        },
      })
    })

    const nodes: ElementDefinition[] = Array.from(nodeSet.values()).map((node) => ({
      data: {
        id: node.id,
        label: node.label,
        product: node.product,
      },
    }))

    return [...nodes, ...edges]
  }, [interactions])

  // Cytoscape stylesheet - using 'any' since cytoscape types are complex
  // Responsive: font sizes and node sizes adapt to screen width
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const stylesheet: any[] = useMemo(() => [
    {
      selector: 'node',
      style: {
        'background-color': '#22d3ee', // accent-cyan
        'label': 'data(label)',
        'color': '#ffffff',
        'text-valign': 'bottom',
        'text-halign': 'center',
        'font-size': `${responsiveConfig.nodeFontSize}px`,
        'font-weight': 'bold',
        'text-margin-y': 8,
        'width': responsiveConfig.nodeSize,
        'height': responsiveConfig.nodeSize,
        'border-width': 3,
        'border-color': '#0891b2',
        'text-outline-color': '#1a1a2e',
        'text-outline-width': 3,
      },
    },
    {
      selector: 'node:hover',
      style: {
        'background-color': '#06b6d4',
        'border-color': '#22d3ee',
        'border-width': 3,
        'cursor': 'pointer',
      },
    },
    {
      selector: 'node:selected',
      style: {
        'background-color': '#a855f7', // accent-purple
        'border-color': '#c084fc',
      },
    },
    {
      selector: 'edge',
      style: {
        'width': 'data(width)',
        'curve-style': 'bezier',
        'target-arrow-shape': 'none',
        'opacity': 0.8,
        // Edge label showing ipTM value
        'label': 'data(label)',
        'font-size': `${responsiveConfig.edgeFontSize}px`,
        'font-weight': 'bold',
        'color': '#ffffff',
        'text-background-color': '#1a1a2e',
        'text-background-opacity': 0.9,
        'text-background-padding': '4px',
        'text-rotation': 'autorotate',
        // Default edge color - will be overridden per-edge in handleCyInit
        'line-color': '#ef886b',
      },
    },
    {
      selector: 'edge[?is_homo]', // Homodimer edges - use loop style
      style: {
        'curve-style': 'bezier',
        'loop-direction': '0deg',
        'loop-sweep': '90deg',
      },
    },
    {
      selector: 'edge:hover',
      style: {
        'line-color': '#22c55e', // accent-green on hover
        'opacity': 1,
        'width': 'mapData(iptm, 0.6, 1.0, 5, 16)',
        'cursor': 'pointer',
        'font-size': `${responsiveConfig.edgeFontSize + 2}px`,
        'font-weight': 'bold',
      },
    },
    {
      selector: 'edge:selected',
      style: {
        'line-color': '#22c55e',
        'opacity': 1,
      },
    },
  ], [responsiveConfig])

  // Layout configuration - circular layout like the reference SVG
  const layout = useMemo(() => ({
    name: 'circle',
    animate: true,
    animationDuration: 500,
    animationEasing: 'ease-out',
    // Fit to viewport
    fit: true,
    // Padding around the circle
    padding: 50,
    // Start angle in radians (top = -PI/2)
    startAngle: -Math.PI / 2,
    // Whether to go clockwise
    clockwise: true,
    // Radius of the circle (undefined = auto-calculate)
    radius: undefined,
    // Avoid label overlap
    avoidOverlap: true,
    // Node dimensions include labels
    nodeDimensionsIncludeLabels: true,
    // Space between nodes (in degrees)
    spacingFactor: 1.2,
  }), [])

  // Handle node click
  const handleNodeClick = useCallback((proteinId: string) => {
    navigate(`/proteins/${clusterId}/${proteinId}`)
  }, [navigate, clusterId])

  // Handle edge click
  const handleEdgeClick = useCallback((edgeData: {
    protein1_id: string
    protein2_id: string
    iptm: number
    ipsae: number
    ipsae_min: number
    is_homo: boolean
  }) => {
    const { protein1_id, protein2_id } = edgeData
    navigate(`/clusters/${clusterId}/complex/${protein1_id}/${protein2_id}`)
  }, [navigate, clusterId])

  // Setup event listeners - only runs once when cytoscape initializes
  const handleCyInit = useCallback((cy: Core) => {
    // Skip if already initialized with same cytoscape instance
    if (initializedRef.current && cyRef.current === cy) {
      return
    }

    cyRef.current = cy
    initializedRef.current = true

    // Apply individual colors to each edge based on their ipTM value
    cy.edges().forEach((edge) => {
      const color = edge.data('color')
      if (color) {
        edge.style('line-color', color)
      }
    })

    // Node click
    cy.on('tap', 'node', (evt) => {
      const node = evt.target
      handleNodeClick(node.id())
    })

    // Node hover - show tooltip with product info
    cy.on('mouseover', 'node', (evt) => {
      const node = evt.target
      if (containerRef.current) {
        const renderedPosition = node.renderedPosition()
        setTooltip({
          visible: true,
          x: renderedPosition.x,
          y: renderedPosition.y - 50, // Position above node
          proteinId: node.id(),
          product: node.data('product') || 'Unknown',
        })
      }
    })

    cy.on('mouseout', 'node', () => {
      setTooltip(prev => ({ ...prev, visible: false }))
    })

    // Edge click
    cy.on('tap', 'edge', (evt) => {
      const edge = evt.target
      handleEdgeClick({
        protein1_id: edge.data('protein1_id'),
        protein2_id: edge.data('protein2_id'),
        iptm: edge.data('iptm'),
        ipsae: edge.data('ipsae'),
        ipsae_min: edge.data('ipsae_min'),
        is_homo: edge.data('is_homo'),
      })
    })

    // Run layout with callback to fit after completion
    const layoutInstance = cy.layout(layout)
    layoutInstance.on('layoutstop', () => {
      // Fit to view with padding after layout completes
      cy.fit(undefined, 40)
      cy.center()
    })
    layoutInstance.run()
  }, [handleNodeClick, handleEdgeClick, layout])

  // Zoom controls
  const handleZoomIn = useCallback(() => {
    if (cyRef.current) {
      cyRef.current.zoom(cyRef.current.zoom() * 1.2)
    }
  }, [])

  const handleZoomOut = useCallback(() => {
    if (cyRef.current) {
      cyRef.current.zoom(cyRef.current.zoom() / 1.2)
    }
  }, [])

  const handleFit = useCallback(() => {
    if (cyRef.current) {
      cyRef.current.fit(undefined, 30)
    }
  }, [])

  // No useEffect for layout - it's handled in handleCyInit only once

  if (interactions.length === 0) {
    return (
      <div className={`flex items-center justify-center h-64 bg-white/5 rounded-lg ${className}`}>
        <p className="text-gray-400">No protein interactions found</p>
      </div>
    )
  }

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Tooltip for node hover */}
      {tooltip.visible && (
        <div
          className="absolute z-20 bg-dark-bg-primary border border-white/10 rounded-lg px-3 py-2 shadow-lg pointer-events-none"
          style={{
            left: tooltip.x,
            top: tooltip.y,
            transform: 'translate(-50%, -100%)',
            maxWidth: '250px',
          }}
        >
          <div className="text-accent-cyan font-mono text-sm font-medium">{tooltip.proteinId}</div>
          <div className="text-gray-300 text-xs mt-1">{tooltip.product}</div>
        </div>
      )}

      {/* Zoom controls */}
      <div className="absolute top-2 right-2 z-10 flex flex-col gap-1">
        <Button
          variant="secondary"
          size="sm"
          onClick={handleZoomIn}
          className="p-2"
          title="Zoom In"
        >
          <ZoomIn className="w-4 h-4" />
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={handleZoomOut}
          className="p-2"
          title="Zoom Out"
        >
          <ZoomOut className="w-4 h-4" />
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={handleFit}
          className="p-2"
          title="Fit to View"
        >
          <Maximize2 className="w-4 h-4" />
        </Button>
      </div>

      {/* Legend - scales with screen size */}
      <div
        className="absolute bottom-2 left-2 z-10 bg-dark-bg-secondary/90 rounded-lg p-2 lg:p-3"
        style={{ transform: `scale(${responsiveConfig.legendScale})`, transformOrigin: 'bottom left' }}
      >
        <div className="flex items-center gap-2 mb-1.5">
          <div className="w-3 h-3 lg:w-4 lg:h-4 rounded-full bg-accent-cyan" />
          <span className="text-gray-300 text-xs lg:text-sm">Protein (click to view)</span>
        </div>
        <div className="flex items-center gap-2 mb-1.5">
          <div className="w-6 h-1.5 lg:w-8 lg:h-2 rounded" style={{ backgroundColor: '#c53334' }} />
          <span className="text-gray-300 text-xs lg:text-sm">High ipTM (≥0.9)</span>
        </div>
        <div className="flex items-center gap-2 mb-1.5">
          <div className="w-6 h-1.5 lg:w-8 lg:h-2 rounded" style={{ backgroundColor: '#ef886b' }} />
          <span className="text-gray-300 text-xs lg:text-sm">Medium ipTM (0.75-0.9)</span>
        </div>
        <div className="flex items-center gap-2 mb-1.5">
          <div className="w-6 h-1.5 lg:w-8 lg:h-2 rounded" style={{ backgroundColor: '#f5c2aa' }} />
          <span className="text-gray-300 text-xs lg:text-sm">Low ipTM (0.6-0.75)</span>
        </div>
        <div className="flex items-center gap-2 mb-1.5">
          <div className="w-6 h-1.5 lg:w-8 lg:h-2 rounded" style={{ backgroundColor: '#3b82f6' }} />
          <span className="text-gray-300 text-xs lg:text-sm">Homodimer</span>
        </div>
        <p className="text-gray-400 text-xs lg:text-sm mt-1.5">Click edge for complex details</p>
      </div>

      {/* Cytoscape container - height adapts to screen resolution */}
      <CytoscapeComponent
        elements={elements}
        stylesheet={stylesheet}
        style={{ width: '100%', height: `${responsiveConfig.graphHeight}px`, background: 'rgba(255,255,255,0.02)', borderRadius: '0.5rem' }}
        cy={handleCyInit}
        wheelSensitivity={0.2}
        boxSelectionEnabled={false}
        autounselectify={false}
        userZoomingEnabled={true}
        userPanningEnabled={true}
        minZoom={0.3}
        maxZoom={3}
      />
    </div>
  )
}

export default InteractionNetwork
